import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, PaperPlaneTilt, Lightning, CalendarBlank } from "@phosphor-icons/react";
import LocationCombobox from "./LocationCombobox";

/**
 * Compact hero lead-capture form.
 * `variant="light"` renders on cream (Home light section) — kept for future non-hero uses.
 * `variant="dark"` renders on the navy hero — default.
 */
export default function LeadForm({ islandSlug = "aruba", variant = "dark" }) {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    asap: true,
    pickup_datetime: "",
    pickup_location: "",
    dropoff_location: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { request_type }

  const isDark = variant === "dark";
  const cardCls = isDark
    ? "border border-gold-bright/40 bg-navy-deep/75 backdrop-blur-md text-offwhite"
    : "border border-cream2 bg-cream text-navy";
  const inputCls = isDark
    ? "w-full bg-transparent border-b border-offwhite/25 focus:border-gold-bright focus:outline-none py-2.5 text-offwhite placeholder:text-offwhite/40 text-sm"
    : "w-full bg-transparent border-b border-navy/20 focus:border-gold focus:outline-none py-2.5 text-navy placeholder:text-navy/40 text-sm";
  const labelCls = isDark
    ? "text-[10px] uppercase tracking-[0.24em] text-offwhite/60"
    : "text-[10px] uppercase tracking-[0.24em] text-navy/60";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim() || !form.pickup_location.trim() || !form.dropoff_location.trim()) {
      toast.error("Please fill in name, contact, pickup and drop-off.");
      return;
    }
    if (!form.asap && !form.pickup_datetime.trim()) {
      toast.error("Choose a pickup date & time, or check 'As soon as possible'.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads/form", { ...form, island_slug: islandSlug });
      setDone({ request_type: data.request_type });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try WhatsApp or phone.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className={`${cardCls} p-8`} data-testid="lead-form-confirmation">
        <CheckCircle size={40} weight="thin" className={isDark ? "text-gold-bright" : "text-teal"} />
        <div className={`mt-4 text-[10px] uppercase tracking-[0.28em] ${isDark ? "text-gold-bright" : "text-teal"}`}>
          Request received
        </div>
        <div className={`mt-2 font-display text-3xl leading-tight ${isDark ? "text-offwhite" : "text-navy"}`}>
          Got it — we'll confirm your ride shortly.
        </div>
        <p className={`mt-4 text-sm leading-relaxed ${isDark ? "text-offwhite/70" : "text-navy/70"}`}>
          {done.request_type === "now"
            ? "You marked this as ASAP — expect a callback within minutes on the contact you provided."
            : "Your reservation is logged. We'll reach out ahead of your pickup to confirm details."}
        </p>
        <button
          onClick={() => { setDone(null); setForm({ name: "", contact: "", asap: true, pickup_datetime: "", pickup_location: "", dropoff_location: "" }); }}
          data-testid="lead-form-reset"
          className={`mt-6 text-[10px] uppercase tracking-[0.28em] underline-offset-4 hover:underline ${isDark ? "text-offwhite/70" : "text-navy/70"}`}
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`${cardCls} p-6 sm:p-8`} data-testid="lead-form">
      <div className={`text-[10px] uppercase tracking-[0.28em] ${isDark ? "text-gold-bright" : "text-teal"} mb-2`}>
        Book online — form
      </div>
      <div className={`font-display text-2xl ${isDark ? "text-offwhite" : "text-navy"} leading-tight mb-6`}>
        Send us your pickup — we'll confirm.
      </div>

      <div className="grid sm:grid-cols-2 gap-x-5 gap-y-4">
        <div>
          <label className={labelCls}>Name</label>
          <input
            required
            data-testid="lead-form-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputCls}
            placeholder="Your name"
          />
        </div>
        <div>
          <label className={labelCls}>Phone or WhatsApp</label>
          <input
            required
            data-testid="lead-form-contact"
            value={form.contact}
            onChange={(e) => setForm({ ...form, contact: e.target.value })}
            className={`${inputCls} font-mono`}
            placeholder="+1 297 555 1234"
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Pickup date &amp; time</label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-1">
            <label className={`inline-flex items-center gap-2 cursor-pointer ${isDark ? "text-offwhite/85" : "text-navy/85"}`} data-testid="lead-form-asap-label">
              <input
                type="checkbox"
                data-testid="lead-form-asap"
                checked={form.asap}
                onChange={(e) => setForm({ ...form, asap: e.target.checked })}
                className="accent-teal w-4 h-4"
              />
              <Lightning size={14} weight="thin" className={isDark ? "text-teal-bright" : "text-teal"} />
              <span className="text-xs uppercase tracking-[0.2em]">As soon as possible</span>
            </label>
            <div className="relative flex-1">
              <CalendarBlank
                size={16}
                weight="thin"
                className={`pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 ${
                  form.asap ? "opacity-30" : ""
                } ${isDark ? "text-teal-bright" : "text-teal"}`}
                aria-hidden
              />
              <input
                type="datetime-local"
                data-testid="lead-form-datetime"
                disabled={form.asap}
                value={form.pickup_datetime}
                onChange={(e) => setForm({ ...form, pickup_datetime: e.target.value })}
                style={{ colorScheme: isDark ? "dark" : "light" }}
                className={`${inputCls} pl-6 pr-2 disabled:opacity-40 disabled:cursor-not-allowed date-teal`}
              />
            </div>
          </div>
        </div>
        <div>
          <label className={labelCls}>Pickup location</label>
          <LocationCombobox
            variant={variant}
            value={form.pickup_location}
            onChange={(v) => setForm({ ...form, pickup_location: v })}
            placeholder="Hotel, villa, Airbnb, or address"
            testId="lead-form-pickup"
          />
        </div>
        <div>
          <label className={labelCls}>Drop-off location</label>
          <LocationCombobox
            variant={variant}
            value={form.dropoff_location}
            onChange={(v) => setForm({ ...form, dropoff_location: v })}
            placeholder="e.g. Divi Aruba, San Nicolas, Palm Beach"
            testId="lead-form-dropoff"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="lead-form-submit"
        className={`group mt-8 w-full inline-flex items-center justify-center gap-3 px-6 py-4 text-xs uppercase tracking-[0.28em] transition-colors disabled:opacity-60
          ${isDark ? "bg-gold-bright text-navy-deep hover:bg-gold" : "bg-navy text-cream hover:bg-gold hover:text-navy"}`}
      >
        <PaperPlaneTilt size={16} weight="thin" />
        {submitting ? "Sending…" : "Get my ride confirmed"}
      </button>
      <div className={`mt-3 text-[10px] uppercase tracking-[0.22em] ${isDark ? "text-offwhite/50" : "text-navy/50"}`}>
        No account · no payment now · manual confirmation
      </div>
    </form>
  );
}
